import { useQuery } from '@tanstack/react-query';
import useGetOffering from './useGetOffering';
import { getCurrentDraftVersionRequest, getPitchDeck } from 'services/offerings';
import { stripFroalaEditorChrome } from 'utils/sanitizeFroalaHtml';
import { getLegalDocumentUrl } from 'components/Offering/Terms/legalDocumentUrls';

const LEGAL_DOCUMENT_LABEL_TO_FIELD = {
  'offering circular': 'offeringCircularUrl',
  'risk factors': 'associatedRisksUrl',
};
const REASONS_TO_INVEST_TYPO_PATTERN = /reason['’]s to invest/gi;

const normalizeAnchorText = (text) =>
  String(text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isSecGovHref = (href) => {
  try {
    return /(^|\.)sec\.gov$/i.test(new URL(href, 'https://www.startengine.com').hostname);
  } catch {
    return false;
  }
};

const getReplacementDocumentUrl = (field, offeringData) => {
  if (field === 'offeringCircularUrl') {
    return getLegalDocumentUrl(offeringData?.offeringCircularUrl, {
      offeringSlug: offeringData?.slug,
      rejectOfferingSelfLink: true,
    });
  }

  if (field === 'associatedRisksUrl') {
    return getLegalDocumentUrl(offeringData?.associatedRisksUrl);
  }

  return '';
};

export const normalizePitchDeckLegalDocumentLinks = (html, offeringData) => {
  if (typeof html !== 'string' || !html || typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  let changed = false;
  template.content.querySelectorAll('a[href]').forEach((anchor) => {
    const currentHref = anchor.getAttribute('href');
    const field = LEGAL_DOCUMENT_LABEL_TO_FIELD[normalizeAnchorText(anchor.textContent)];
    if (!currentHref || !field || !isSecGovHref(currentHref)) return;

    const replacementHref = getReplacementDocumentUrl(field, offeringData);
    if (!replacementHref || replacementHref === currentHref) return;

    anchor.setAttribute('href', replacementHref);
    changed = true;
  });

  const textWalker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  let textNode = textWalker.nextNode();
  while (textNode) {
    const nextText = textNode.nodeValue.replace(REASONS_TO_INVEST_TYPO_PATTERN, (match) =>
      match === match.toUpperCase() ? 'REASONS TO INVEST' : 'Reasons to Invest',
    );
    if (nextText !== textNode.nodeValue) {
      textNode.nodeValue = nextText;
      changed = true;
    }
    textNode = textWalker.nextNode();
  }

  return changed ? template.innerHTML : html;
};

const useGetPitchDeck = ({ preview = false, slug }) => {
  const { data: offeringData } = useGetOffering({ slug });

  const normalizePitchDeckResponse = (pitchDeck) => {
    if (!pitchDeck || typeof pitchDeck.html !== 'string') return pitchDeck;

    return {
      ...pitchDeck,
      html: normalizePitchDeckLegalDocumentLinks(pitchDeck.html, offeringData),
    };
  };

  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: preview
      ? ['pitchDeck', 'preview', preview]
      : ['pitchDeck', 'live', offeringData?.primaryId],
    queryFn: preview
      ? async () => {
          const html = await getCurrentDraftVersionRequest({
            campaignId: preview,
          });

          return {
            html: normalizePitchDeckLegalDocumentLinks(
              stripFroalaEditorChrome(html?.campaignVersion?.htmlText),
              offeringData,
            ),
          };
        }
      : () =>
          getPitchDeck({
            primaryId: offeringData?.primaryId,
          }).then(normalizePitchDeckResponse),
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * (60 * 1000),
    enabled: preview ? Boolean(preview) : Boolean(offeringData?.primaryId),
  });

  return {
    data,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useGetPitchDeck;
